import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authApi } from '../api/authApi';
import { marketDataApi } from '../api/marketDataApi';
import { useAuth } from './AuthContext';

const AlertsContext = createContext(null);
const STORAGE_KEY = 'lt-price-alerts';
const CHECK_MS = 20000;

const loadLocal = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
};

const persistLocal = (alerts) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(alerts));
  } catch {
    // ignore
  }
};

const midFromQuote = (quote) => {
  if (!quote) return null;
  const bid = Number(quote.bidPrice);
  const ask = Number(quote.askPrice);
  if (Number.isFinite(bid) && Number.isFinite(ask)) return (bid + ask) / 2;
  if (Number.isFinite(bid)) return bid;
  if (Number.isFinite(ask)) return ask;
  return null;
};

const notifyBrowser = (title, body) => {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body, tag: title });
    }
  } catch {
    // ignore
  }
};

export const AlertsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [alerts, setAlerts] = useState(() => loadLocal());
  const [hydrated, setHydrated] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const firedRef = useRef(new Set());

  useEffect(() => {
    if (!isAuthenticated) {
      setHydrated(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await authApi.getPreferences();
        const remote = response.data?.priceAlerts;
        if (!cancelled && Array.isArray(remote)) {
          setAlerts(remote);
          persistLocal(remote);
        }
      } catch {
        // keep local
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    persistLocal(alerts);
    if (!hydrated || !isAuthenticated) return undefined;
    const timer = setTimeout(() => {
      authApi.savePreferences({ priceAlerts: alerts }).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [alerts, hydrated, isAuthenticated]);

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    try {
      return await Notification.requestPermission();
    } catch {
      return 'denied';
    }
  }, []);

  const emitEvent = useCallback(({ title, detail, symbol } = {}) => {
    const text = String(detail || title || 'Alert');
    notifyBrowser(title || 'Alert', text);
    setLastEvent({
      id: `sys-${Date.now()}`,
      symbol: String(symbol || '').toUpperCase(),
      detail: text,
      at: new Date().toISOString(),
      title: title || 'Alert',
    });
  }, []);

  const addAlert = useCallback(async ({ symbol, type, threshold }) => {
    const normalized = String(symbol || '').trim().toUpperCase();
    const value = Number(threshold);
    if (!normalized || !Number.isFinite(value)) return null;
    await requestPermission();
    const next = {
      id: `${normalized}-${type}-${Date.now()}`,
      symbol: normalized,
      type,
      threshold: value,
      active: true,
      triggeredAt: null,
      createdAt: new Date().toISOString(),
    };
    setAlerts((prev) => [next, ...prev].slice(0, 100));
    return next;
  }, [requestPermission]);

  const removeAlert = useCallback((id) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
    firedRef.current.delete(id);
  }, []);

  const clearTriggered = useCallback(() => {
    setAlerts((prev) => prev.map((a) => ({ ...a, triggeredAt: null, active: true })));
    firedRef.current.clear();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let cancelled = false;

    const check = async () => {
      const active = alerts.filter((a) => a.active && !a.triggeredAt);
      if (!active.length) return;

      const symbols = [...new Set(active.map((a) => a.symbol))];
      const quoteMap = {};
      const dayMap = {};

      await Promise.all(
        symbols.map(async (symbol) => {
          try {
            const [quoteRes, barsRes] = await Promise.all([
              marketDataApi.getQuote(symbol),
              marketDataApi.getBars(symbol, { timeframe: '1Day', limit: 2 }),
            ]);
            quoteMap[symbol] = quoteRes.data || null;
            const bars = barsRes.data || [];
            const latest = bars[bars.length - 1] || null;
            const previous = bars.length > 1 ? bars[bars.length - 2] : null;
            dayMap[symbol] = {
              open: latest?.open ?? null,
              prevClose: previous?.close ?? latest?.open ?? null,
            };
          } catch {
            // skip symbol
          }
        })
      );

      if (cancelled) return;

      const updates = [];
      active.forEach((alert) => {
        if (firedRef.current.has(alert.id)) return;
        const mid = midFromQuote(quoteMap[alert.symbol]);
        if (mid == null) return;

        const day = dayMap[alert.symbol] || {};
        const baseline = Number(day.prevClose) > 0 ? Number(day.prevClose) : Number(day.open);
        const pct = baseline ? ((mid - baseline) / baseline) * 100 : null;

        let hit = false;
        let detail = '';
        if (alert.type === 'price_above' && mid >= alert.threshold) {
          hit = true;
          detail = `${alert.symbol} at $${mid.toFixed(2)} (≥ $${alert.threshold})`;
        } else if (alert.type === 'price_below' && mid <= alert.threshold) {
          hit = true;
          detail = `${alert.symbol} at $${mid.toFixed(2)} (≤ $${alert.threshold})`;
        } else if (alert.type === 'pct_up' && pct != null && pct >= alert.threshold) {
          hit = true;
          detail = `${alert.symbol} +${pct.toFixed(2)}% today (≥ +${alert.threshold}%)`;
        } else if (alert.type === 'pct_down' && pct != null && pct <= -Math.abs(alert.threshold)) {
          hit = true;
          detail = `${alert.symbol} ${pct.toFixed(2)}% today (≤ -${Math.abs(alert.threshold)}%)`;
        }

        if (hit) {
          firedRef.current.add(alert.id);
          updates.push(alert.id);
          notifyBrowser('Price alert', detail);
          setLastEvent({ id: alert.id, symbol: alert.symbol, detail, at: new Date().toISOString() });
        }
      });

      if (updates.length) {
        setAlerts((prev) =>
          prev.map((a) =>
            updates.includes(a.id)
              ? { ...a, active: false, triggeredAt: new Date().toISOString() }
              : a
          )
        );
      }
    };

    check();
    const interval = setInterval(check, CHECK_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [alerts, isAuthenticated]);

  const value = useMemo(
    () => ({
      alerts,
      lastEvent,
      clearLastEvent: () => setLastEvent(null),
      addAlert,
      removeAlert,
      clearTriggered,
      requestPermission,
      emitEvent,
    }),
    [alerts, lastEvent, addAlert, removeAlert, clearTriggered, requestPermission, emitEvent]
  );

  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
};

export const useAlerts = () => {
  const ctx = useContext(AlertsContext);
  if (!ctx) throw new Error('useAlerts must be used within AlertsProvider');
  return ctx;
};
